// src/components/sensors/PlaceSensorModal.js
// Enhanced Place Sensor Modal with Polymorphic Pipeline and Asset Linking

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Select,
  VStack,
  Text,
  Alert,
  AlertIcon,
  AlertDescription,
  Box,
  Divider,
  HStack,
  Icon,
} from '@chakra-ui/react';
import { MdLocationOn } from 'react-icons/md';

function PlaceSensorModal({
  isOpen,
  onClose,
  onSuccess,
  coordinates,
  isLoading,
  pipelines = [],
  assets = [], // --- UPDATE: Added assets prop ---
  enablePipelineLinking = false, // Kept for backward compatibility, now acts as general linking toggle
  availableSensors = [],
}) {
  const [selectedSensorId, setSelectedSensorId] = useState('');
  
  // --- UPDATE: Replaced binary pipeline state with dynamic polymorphic states ---
  const [linkType, setLinkType] = useState('none'); 
  const [selectedEntityId, setSelectedEntityId] = useState('');
  // --- END UPDATE ---
  
  const [validationError, setValidationError] = useState('');

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedSensorId('');
      setLinkType('none');
      setSelectedEntityId('');
      setValidationError('');
    }
  }, [isOpen]);

  const handleSubmit = () => {
    // Validation
    if (!selectedSensorId) {
      setValidationError('Please select a sensor');
      return;
    }

    if (!coordinates || !coordinates.lat || !coordinates.lng) {
      setValidationError('Invalid coordinates');
      return;
    }

    // --- UPDATE: Validate based on linkType ---
    if (linkType !== 'none' && !selectedEntityId) {
      setValidationError(`Please select a specific ${linkType} to link`);
      return;
    }
    // --- END UPDATE ---

    setValidationError('');

    // Prepare submission data
    const submissionData = {
      deviceId: selectedSensorId,
      coordinates: {
        lat: coordinates.lat,
        lng: coordinates.lng,
      },
    };

    // --- UPDATE: Add polymorphic linking payload ---
    if (linkType !== 'none' && selectedEntityId) {
      submissionData.linked_entity_type = linkType;
      submissionData.linked_entity_id = selectedEntityId;
      
      // Preserve pipelineId for backward compatibility with older parent components
      if (linkType === 'pipeline') {
          submissionData.pipelineId = selectedEntityId;
      }
    }
    // --- END UPDATE ---

    // Call success handler
    onSuccess(submissionData);
  };

  const handleCancel = () => {
    setValidationError('');
    onClose();
  };

  // --- UPDATE: Helper to filter assets based on selected type ---
  // --- UPDATE (wells v1.2): "Drainage Well" assets are offered under "Link to Well"
  const isWellFamily = (t) => /^(well|(drainage|storm|soak ?away|disposal|recharge) ?well)$/.test(String(t || '').trim().toLowerCase().replace(/[_-]+/g, ' '));
  const filteredAssets = assets.filter(
      (asset) => asset.type && (linkType === 'well' ? isWellFamily(asset.asset_type || asset.type) : asset.type.toLowerCase() === linkType)
  );
  // --- END UPDATE
  // --- END UPDATE ---

  return (
    <Modal isOpen={isOpen} onClose={handleCancel} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Place Sensor</ModalHeader>
        <ModalCloseButton />
        
        <ModalBody>
          <VStack spacing={4} align="stretch">
            {/* Coordinates Display */}
            {coordinates && (
              <Box
                p={3}
                bg="blue.50"
                borderRadius="md"
                borderWidth="1px"
                borderColor="blue.200"
              >
                <HStack spacing={2} mb={2}>
                  <Icon as={MdLocationOn} color="blue.500" />
                  <Text fontWeight="bold" fontSize="sm">
                    Selected Location
                  </Text>
                </HStack>
                <Text fontSize="sm" fontFamily="mono">
                  Lat: {coordinates.lat?.toFixed(6)}, Lng: {coordinates.lng?.toFixed(6)}
                </Text>
              </Box>
            )}

            {/* Sensor Selection */}
            <FormControl isRequired>
              <FormLabel>Select Sensor</FormLabel>
              <Select
                placeholder="Choose a sensor..."
                value={selectedSensorId}
                onChange={(e) => setSelectedSensorId(e.target.value)}
                isDisabled={isLoading}
              >
                {availableSensors.length > 0 ? (
                  availableSensors.map(sensor => (
                    <option
                      key={sensor.device_id || sensor.id}
                      value={sensor.device_id || sensor.id}
                    >
                      {sensor.name || `Sensor ${sensor.device_id || sensor.id}`}
                      {sensor.latitude && sensor.longitude && ' (Already placed)'}
                    </option>
                  ))
                ) : (
                  <option disabled>No sensors available</option>
                )}
              </Select>
            </FormControl>

            {/* --- UPDATE: Enhanced Polymorphic Linking Option --- */}
            {enablePipelineLinking && (
              <>
                <Divider />
                
                <FormControl>
                  <FormLabel>Linking Options</FormLabel>
                  <Select 
                    value={linkType} 
                    onChange={(e) => {
                        setLinkType(e.target.value);
                        setSelectedEntityId(''); // Reset selection when type changes
                    }}
                    isDisabled={isLoading}
                  >
                    <option value="none">Do Not Link (Standalone Sensor)</option>
                    <option value="pipeline">Link to Pipeline</option>
                    <option value="pump">Link to Pump</option>
                    <option value="valve">Link to Valve</option>
                    <option value="tank">Link to Tank</option>
                    <option value="well">Link to Well</option>
                    <option value="meter">Link to Meter</option>
                    <option value="hydrant">Link to Hydrant</option>
                    <option value="facility">Link to Facility</option>
                  </Select>
                </FormControl>

                {linkType === 'pipeline' && (
                  <FormControl>
                    <FormLabel>Select Pipeline</FormLabel>
                    <Select
                      placeholder="Choose a pipeline..."
                      value={selectedEntityId}
                      onChange={(e) => setSelectedEntityId(e.target.value)}
                      isDisabled={isLoading}
                    >
                      {pipelines.map(pipeline => (
                        <option key={pipeline.id} value={pipeline.id}>
                          {pipeline.name || `Pipeline ${pipeline.id}`}
                          {pipeline.length && ` (${pipeline.length.toFixed(0)}m)`}
                        </option>
                      ))}
                    </Select>
                  </FormControl>
                )}

                {['pump', 'valve', 'tank', 'well', 'meter', 'hydrant', 'facility'].includes(linkType) && (
                  <FormControl>
                    <FormLabel>Select {linkType.charAt(0).toUpperCase() + linkType.slice(1)}</FormLabel>
                    <Select
                      placeholder={`Choose a ${linkType}...`}
                      value={selectedEntityId}
                      onChange={(e) => setSelectedEntityId(e.target.value)}
                      isDisabled={isLoading}
                    >
                      {filteredAssets.length > 0 ? (
                          filteredAssets.map(asset => (
                            <option key={asset.id} value={asset.id}>
                              {asset.name || `${linkType} ${asset.id}`}
                            </option>
                          ))
                      ) : (
                          <option disabled>No {linkType}s available</option>
                      )}
                    </Select>
                  </FormControl>
                )}

                {linkType !== 'none' && (
                  <Alert status="info" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">
                      The sensor will be permanently linked to the selected {linkType} after placement.
                    </AlertDescription>
                  </Alert>
                )}
              </>
            )}
            {/* --- END UPDATE --- */}

            {/* Validation Error */}
            {validationError && (
              <Alert status="error" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{validationError}</AlertDescription>
              </Alert>
            )}

            {/* Info */}
            <Alert status="info" borderRadius="md">
              <AlertIcon />
              <Box>
                <AlertDescription fontSize="sm">
                  This will update the sensor's location in the system.
                  {linkType !== 'none' && ` The sensor will also be linked to the selected ${linkType}.`}
                </AlertDescription>
              </Box>
            </Alert>
          </VStack>
        </ModalBody>

        <ModalFooter>
          <Button
            variant="ghost"
            mr={3}
            onClick={handleCancel}
            isDisabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            onClick={handleSubmit}
            isLoading={isLoading}
            loadingText="Placing..."
          >
            Place Sensor
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

export default PlaceSensorModal;